import { type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import {
  PolicyKey,
  PolicyMode,
  type PolicyModeType,
} from '@kirin/api-client/contracts/policies';
import {
  useOrganizationPolicies,
  useToggleOrganizationPolicy,
} from '@/hooks/api/useOrganizationPolicies';
import { useRoleCheck } from '@/hooks/useRoleCheck';
import { OrganizationRole } from '@/lib/roles';
import { useToast } from '@/hooks/use-toast';
import { createMutationHandler } from '@/hooks/mutationHelpers';
import { MODE_LABELS } from '@/components/policylist/constants';
import { SecureAiCodingEnforcementPage } from '@/components/AiInstructionCatalog/SecureAiCodingEnforcementPage';

/**
 * KIR-3816: organization Secure AI Coding Enforcement page. Reads/writes the
 * `ai-instruction-catalog` policy mode via the org policy hooks (owner/manager
 * may edit) and renders the shared enforcement page.
 */
export default function SecureAiCodingTab(): ReactElement | null {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { hasAccess } = useRoleCheck({
    param: 'organizationId',
    anyOf: [OrganizationRole.Owner, OrganizationRole.Manager],
  });

  const { data: policies } = useOrganizationPolicies(organizationId);
  const togglePolicy = useToggleOrganizationPolicy(organizationId);
  const { toast } = useToast();

  if (!organizationId) {
    return null;
  }

  const policy = policies?.find(p => p.key === PolicyKey.AiInstructionCatalog);
  const mode = (policy?.mode ?? PolicyMode.Off) as PolicyModeType;
  const policyName = policy?.name ?? 'Secure AI Coding Enforcement';

  // Route through createMutationHandler so a mode change shows the same
  // success/error toast as every other policy on the Policies page.
  const onModeChange = (next: PolicyModeType): void => {
    void createMutationHandler(togglePolicy, toast, {
      successMessage: `Policy ${policyName} set to ${MODE_LABELS[next]}`,
      errorMessage: `Failed to update ${policyName}`,
    })({
      policyKey: PolicyKey.AiInstructionCatalog,
      enabled: next !== PolicyMode.Off,
      mode: next,
    });
  };

  return (
    <SecureAiCodingEnforcementPage
      organizationId={organizationId}
      canEdit={hasAccess}
      mode={mode}
      supportsEnforce={policy?.supportsEnforce ?? false}
      enforceDisabledReason={policy?.enforceDisabledReason}
      enforceTooltip={policy?.enforceTooltip}
      onModeChange={onModeChange}
      modePending={togglePolicy.isPending}
    />
  );
}
